import React, { useState, useRef, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { routes } from "constants/routes";
import { ReactComponent as Logo } from "assets/RewardsLogo.svg";
import ForgotPasswordForm from "components/ForgotPassword/ForgotPasswordForm";
import ResetPasswordForm from "components/ForgotPassword/ResetPasswordForm";
import loginImage from "assets/login-image.png";
import Button from "components/Buttons/Button";
import {
  useForgotPasswordMutation,
  useVerifyForgotPasswordMutation,
  usePasswordUpdateMutation,
  useResendForgotPasswordCodeMutation,
} from "store/apis/forgotPasswordApi";
import { IForgotPasswordProps } from "./types";
import Render from "components/Common/Render";

// Verification Step Component
interface VerificationStepProps {
  email: string;
  onVerify: (code: string) => Promise<void>;
  isVerifying: boolean;
  onBackToLogin: () => void;
  onResendCode: () => void;
  isResending: boolean;
}

const VerificationStep: React.FC<VerificationStepProps> = ({
  email,
  onVerify,
  isVerifying,
  onBackToLogin,
  onResendCode,
  isResending,
}) => {
  const [code, setCode] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError("");

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!code[index] && index > 0) {
        inputRefs[index - 1].current?.focus();
      } else if (code[index]) {
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const verificationCode = code.join("");
    if (verificationCode.length === 4) {
      try {
        await onVerify(verificationCode);
      } catch (err) {
        setError("Invalid code");
      }
    }
  };

  const isValid = code.every((digit) => digit !== "");

  return (
    <div className="w-full max-w-[512px] flex flex-col overflow-hidden">
      <div className="flex flex-col gap-6">
        <h1 className="font-poppins font-semibold text-[28px] md:text-[32px] leading-[100%] tracking-[0] text-gray-900">
          Verification
        </h1>

        <p className="font-poppins font-normal text-[14px] md:text-[16px] leading-[100%] tracking-[0] text-gray-600 opacity-75 mb-6">
          Enter your 4 digits code that you received on your email.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-[24px] md:gap-[40px]">
        {/* Code Input Boxes */}
        <div className="flex justify-between gap-2 md:gap-10 w-full">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={`w-[60px] h-[60px] md:w-[72px] md:h-[72px] rounded-[4px] p-3 text-center text-[20px] md:text-[24px] font-poppins font-medium leading-[100%] tracking-[0] text-darkBlack focus:outline-none transition ${
                error
                  ? "border border-redWarning focus:ring-2 focus:ring-redWarning focus:border-redWarning"
                  : "border border-gray-300 opacity-60"
              }`}
            />
          ))}
        </div>

        {/* Error message - Mobile (below inputs) */}
        <Render when={!!error}>
          <div className="flex md:hidden items-center gap-1 text-redWarning text-[14px] font-poppins font-medium">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span>Invalid code</span>
          </div>
        </Render>

        {/* Resend Code and Error Message Row - Desktop */}
        <div className="hidden md:flex items-center justify-between w-full">
          {/* Resend Code Link - Left */}
          <div className="max-w-[340px]">
            <p className="font-poppins font-medium text-[14px] leading-[100%] tracking-[0] text-gray-700 opacity-100">
              Didn't receive a code?{" "}
              <span
                onClick={isResending ? undefined : onResendCode}
                className={`text-[#0102CE] font-medium hover:underline cursor-pointer ${
                  isResending ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isResending ? "Resending..." : "Resend"}
              </span>
            </p>
          </div>

          {/* Error message - Right */}
          <Render when={!!error}>
            <div className="flex items-center gap-1 text-redWarning text-[14px] font-poppins font-medium ml-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Invalid code</span>
            </div>
          </Render>
        </div>

        {/* Resend Code - Mobile only (below error) */}
        <div className="flex md:hidden">
          <p className="font-poppins font-medium text-[14px] leading-[100%] tracking-[0] text-gray-700 opacity-100">
            Didn't receive a code?{" "}
            <span
              onClick={isResending ? undefined : onResendCode}
              className={`text-[#0102CE] font-medium hover:underline cursor-pointer ${
                isResending ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isResending ? "Resending..." : "Resend"}
            </span>
          </p>
        </div>

        {/* Buttons Container */}
        <div className="flex flex-col md:flex-row md:justify-between gap-2 w-full">
          <Button
            type="submit"
            label={isVerifying ? "Verifying..." : "Submit"}
            disabled={!isValid || isVerifying}
            loading={isVerifying}
            variant="primary"
            customClass="!mb-0 !me-0 w-full md:w-[240px] order-1 md:order-2"
          />

          <Button
            type="button"
            label="Back to login"
            onClick={onBackToLogin}
            variant="secondary"
            customClass="!mb-0 !me-0 w-full md:w-[240px] order-2 md:order-1"
          />
        </div>
      </form>
    </div>
  );
};

const ForgotPassword: React.FC<IForgotPasswordProps> = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1: Enter email, 2: Verify code, 3: Reset password, 4: Success
  const [email, setEmail] = useState<string | undefined>();

  const [forgotPassword, { isLoading: isSendingCode }] =
    useForgotPasswordMutation();
  const [verifyForgotPassword, { isLoading: isVerifying }] =
    useVerifyForgotPasswordMutation();
  const [passwordUpdate, { isLoading: isUpdatingPassword }] =
    usePasswordUpdateMutation();
  const [resendCode, { isLoading: isResending }] =
    useResendForgotPasswordCodeMutation();

  // Step 1: Submit email
  const handleForgotPasswordSubmit = async (identifier: string) => {
    try {
      const response = await forgotPassword({ email: identifier }).unwrap();

      // Store the token for subsequent API calls
      if (response.access) {
        localStorage.setItem(
          "forgotPasswordToken",
          JSON.stringify(response.access)
        );
      }

      // Store the email for later use
      setEmail(identifier);

      toast.success(response.message || "Verification code sent to your email");
      setCurrentStep(2); // Move to verification step
    } catch (error: any) {
      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        "Failed to send verification code. Please try again.";
      toast.error(errorMessage);
    }
  };

  // Step 2: Verify OTP code
  const handleVerifyCode = async (code: string) => {
    try {
      const response = await verifyForgotPassword({
        otp_code: code,
        sendTo: "email",
        email: email,
      }).unwrap();

      // Update token if provided
      if (response.access) {
        localStorage.setItem(
          "forgotPasswordToken",
          JSON.stringify(response.access)
        );
      }

      toast.success("Verification successful!");
      setCurrentStep(3); // Move to reset password step
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || error?.status === 401
          ? "Session expired. Please request a new code."
          : "Invalid verification code. Please try again.";
      toast.error(errorMessage);
      throw error;
    }
  };

  // Handle resend code
  const handleResendCode = async () => {
    try {
      if (!email) {
        toast.error("Missing email address. Please restart the process.");
        return;
      }

      const response = await resendCode({
        sendTo: "email",
        email: email,
      }).unwrap();

      toast.success(
        response.message || "Verification code resent successfully!"
      );
    } catch (error: any) {
      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        error?.data?.detail ||
        "Failed to resend code. Please try again.";
      toast.error(errorMessage);
    }
  };

  // Step 3: Update password
  const handlePasswordUpdate = async (password: string) => {
    try {
      const response = await passwordUpdate({
        email: email!,
        password,
      }).unwrap();

      // Clean up stored token
      localStorage.removeItem("forgotPasswordToken");

      toast.success(response.message || "Password updated successfully!");

      // Move to success step
      setCurrentStep(4);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        "Failed to update password. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleBackToLogin = () => {
    localStorage.removeItem("forgotPasswordToken");
    navigate(routes.login);
  };

  return (
    <div className="flex flex-1 h-screen md:h-screen">
      {/* Left Side - Form Content */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-16 bg-white overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8">
            <Logo width={200} height={61} />
          </div>

          {/* Render appropriate step */}
          <Render when={currentStep === 1}>
            <ForgotPasswordForm
              onSubmit={handleForgotPasswordSubmit}
              isLoading={isSendingCode}
              onBackToLogin={handleBackToLogin}
            />
          </Render>

          <Render when={currentStep === 2}>
            <VerificationStep
              email={email || ""}
              onVerify={handleVerifyCode}
              isVerifying={isVerifying}
              onBackToLogin={handleBackToLogin}
              onResendCode={handleResendCode}
              isResending={isResending}
            />
          </Render>

          <Render when={currentStep === 3}>
            <ResetPasswordForm
              onSubmit={handlePasswordUpdate}
              isLoading={isUpdatingPassword}
              onBackToLogin={handleBackToLogin}
            />
          </Render>

          <Render when={currentStep === 4}>
            <div className="text-left flex flex-col items-start w-full">
              {/* Success Icon */}
              <div className="mb-8 w-[60px] h-[60px] flex items-center justify-center">
                <svg
                  width="48.75"
                  height="48.75"
                  viewBox="0 0 49 49"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M35.0766 17.4234C35.2509 17.5976 35.3892 17.8044 35.4836 18.032C35.5779 18.2596 35.6265 18.5036 35.6265 18.75C35.6265 18.9964 35.5779 19.2404 35.4836 19.468C35.3892 19.6956 35.2509 19.9024 35.0766 20.0766L21.9516 33.2016C21.7774 33.3759 21.5706 33.5142 21.343 33.6086C21.1154 33.7029 20.8714 33.7515 20.625 33.7515C20.3786 33.7515 20.1346 33.7029 19.907 33.6086C19.6794 33.5142 19.4726 33.3759 19.2984 33.2016L13.6734 27.5766C13.3216 27.2247 13.124 26.7476 13.124 26.25C13.124 25.7524 13.3216 25.2753 13.6734 24.9234C14.0253 24.5716 14.5025 24.374 15 24.374C15.4976 24.374 15.9747 24.5716 16.3266 24.9234L20.625 29.2242L32.4234 17.4234C32.5976 17.2491 32.8044 17.1108 33.032 17.0165C33.2596 16.9221 33.5036 16.8735 33.75 16.8735C33.9964 16.8735 34.2404 16.9221 34.468 17.0165C34.6956 17.1108 34.9024 17.2491 35.0766 17.4234ZM48.75 24.375C48.75 29.1959 47.3204 33.9086 44.6421 37.917C41.9637 41.9255 38.1569 45.0497 33.7029 46.8946C29.249 48.7394 24.348 49.2222 19.6197 48.2816C14.8914 47.3411 10.5482 45.0196 7.13928 41.6107C3.73038 38.2018 1.40888 33.8586 0.46837 29.1303C-0.472145 24.402 0.0105613 19.501 1.85545 15.0471C3.70033 10.5931 6.82454 6.78629 10.833 4.10793C14.8414 1.42957 19.5541 0 24.375 0C30.8376 0.00682458 37.0335 2.57709 41.6032 7.14681C46.1729 11.7165 48.7432 17.9124 48.75 24.375ZM45 24.375C45 20.2958 43.7904 16.3081 41.5241 12.9164C39.2578 9.5246 36.0366 6.88104 32.2679 5.31998C28.4991 3.75893 24.3521 3.35048 20.3513 4.1463C16.3504 4.94212 12.6754 6.90647 9.79093 9.79092C6.90648 12.6754 4.94213 16.3504 4.14631 20.3513C3.35049 24.3521 3.75894 28.4991 5.32 32.2678C6.88105 36.0366 9.52461 39.2578 12.9164 41.5241C16.3081 43.7904 20.2958 45 24.375 45C29.8432 44.9938 35.0856 42.8188 38.9522 38.9522C42.8188 35.0856 44.9938 29.8432 45 24.375Z"
                    fill="#03A033"
                  />
                </svg>
              </div>

              {/* Heading */}
              <h1 className="font-poppins font-semibold text-[28px] md:text-[32px] leading-[100%] text-gray-900 mb-4 w-full">
                Reset Successful
              </h1>

              {/* Subtext */}
              <p className="font-poppins font-normal text-[14px] md:text-[16px] leading-[100%] text-gray-600 mb-8 w-full">
                Your password has been reset successfully. You can now go back
                and log in with your new password.
              </p>

              {/* Button */}
              <Button
                label="Back to login"
                onClick={handleBackToLogin}
                variant="primary"
                customClass="!mb-0 !me-0 w-full md:w-[512px]"
              />
            </div>
          </Render>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 bg-white items-center justify-center p-6 relative overflow-hidden">
        <div className="w-full h-full flex items-center justify-center relative">
          <div className="w-full h-full rounded-2xl overflow-hidden shadow-lg">
            <img
              src={loginImage}
              alt="Open Rewards"
              className="w-full h-full object-cover"
              style={{ transform: "scale(1.05)" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
